import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import type { Project } from '../../../services/projectManagement/projectsService';
import { pmSectionsApiService, type ProjectSection } from '../../../services/projectManagement/pmSectionsApiService';

interface ProjectSectionsModalProps {
  organizationId: string;
  project: Project;
  onClose: () => void;
  onUpdated?: () => void;
}

const ProjectSectionsModal: React.FC<ProjectSectionsModalProps> = ({ organizationId, project, onClose, onUpdated }) => {
  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState<ProjectSection[]>([]);
  const [sectionName, setSectionName] = useState('');

  const fetchSections = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await pmSectionsApiService.list({ organization_id: organizationId, project_id: project.id });
      if (error) {
        setSections([]);
      } else {
        setSections(data || []);
      }
    } catch {
      setSections([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId, project.id]);

  useEffect(() => {
    void fetchSections();
  }, [fetchSections]);

  const nextPosition = useMemo(() => {
    const max = (sections || []).reduce((acc, s) => Math.max(acc, Number(s.position) || 0), -1);
    return max + 1;
  }, [sections]);

  const handleCreate = async () => {
    if (!sectionName.trim()) {
      toast.error('Section name is required');
      return;
    }

    const { error } = await pmSectionsApiService.create({
      organization_id: organizationId,
      project_id: project.id,
      name: sectionName.trim(),
      position: nextPosition,
    });

    if (error) {
      toast.error(String(error));
      return;
    }

    toast.success('Section created');
    setSectionName('');
    await fetchSections();
    onUpdated?.();
  };

  const handleDelete = async (section: ProjectSection) => {
    if (!window.confirm(`Delete section "${section.name}"?`)) return;

    const { error } = await pmSectionsApiService.delete(section.id, organizationId);
    if (error) {
      toast.error(String(error));
      return;
    }

    toast.success('Section deleted');
    await fetchSections();
    onUpdated?.();
  };

  const moveSection = async (sectionId: string, direction: 'up' | 'down') => {
    const idx = sections.findIndex(s => s.id === sectionId);
    if (idx === -1) return;

    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sections.length) return;

    const next = [...sections];
    const tmp = next[idx];
    next[idx] = next[swapIdx];
    next[swapIdx] = tmp;

    setSections(next);

    const { error } = await pmSectionsApiService.reorder({
      organization_id: organizationId,
      project_id: project.id,
      section_ids: next.map(s => s.id),
    });

    if (error) {
      toast.error(String(error));
      await fetchSections();
      return;
    }

    onUpdated?.();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-3xl rounded-xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Project Sections</h3>
            <div className="mt-1 text-sm text-gray-600">{project.name}</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">✕</button>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-lg border border-gray-200 p-4">
            <div className="text-sm font-semibold text-gray-900 mb-3">Create Section</div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  value={sectionName}
                  onChange={(e) => setSectionName(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>

              <button
                onClick={() => void handleCreate()}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
              >
                Create
              </button>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 p-4">
            <div className="text-sm font-semibold text-gray-900 mb-3">Sections</div>

            {loading ? (
              <div className="text-sm text-gray-500">Loading...</div>
            ) : sections.length === 0 ? (
              <div className="rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-600">
                No sections
              </div>
            ) : (
              <div className="space-y-2">
                {sections.map((s, idx) => (
                  <div key={s.id} className="flex items-center justify-between rounded-lg border border-gray-200 p-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-900 truncate">{s.name}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => void moveSection(s.id, 'up')}
                        className="rounded bg-gray-100 px-2 py-1 text-sm hover:bg-gray-200"
                        disabled={idx === 0}
                      >
                        ↑
                      </button>
                      <button
                        onClick={() => void moveSection(s.id, 'down')}
                        className="rounded bg-gray-100 px-2 py-1 text-sm hover:bg-gray-200"
                        disabled={idx === sections.length - 1}
                      >
                        ↓
                      </button>
                      <button
                        onClick={() => void handleDelete(s)}
                        className="rounded bg-red-50 px-2 py-1 text-sm text-red-700 hover:bg-red-100"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectSectionsModal;
